<template>
  <div class="insights-counts">
    <p class="insights-counts__intro">
      Volume for the selected period.
      <span class="insights-counts__intro-hint">
        Proposals and postings are counted per invoice line; invoices are whole documents.
      </span>
    </p>

    <div class="insights-counts__grid">
      <div class="insights-counts__card">
        <q-skeleton v-if="loading" animation="blink" height="100%" />
        <template v-else>
          <div class="insights-counts__title">
            Proposals (invoice lines)
            <q-icon name="help_outline" size="12px" class="insights-counts__help">
              <q-tooltip max-width="260px">
                Suggestions we made for how to code an invoice line — one count per line, not
                per invoice.
              </q-tooltip>
            </q-icon>
          </div>
          <div class="insights-counts__value">{{ formatNumber(data?.totalProposals) }}</div>
          <div class="insights-counts__footer">
            Last proposal: {{ formatRelative(data?.lastProposalReceivedTime) }}
          </div>
        </template>
      </div>

      <div class="insights-counts__card">
        <q-skeleton v-if="loading" animation="blink" height="100%" />
        <template v-else>
          <div class="insights-counts__title">
            Postings (invoice lines)
            <q-icon name="help_outline" size="12px" class="insights-counts__help">
              <q-tooltip max-width="260px">
                Actual booking entries in your accounting system — whether they came from a
                proposal you accepted or a manual posting. Counted per line.
              </q-tooltip>
            </q-icon>
          </div>
          <div class="insights-counts__value">{{ formatNumber(data?.totalPostings) }}</div>
          <div class="insights-counts__footer">
            Last posting: {{ formatRelative(data?.lastPostingReceivedTime) }}
          </div>
        </template>
      </div>

      <div class="insights-counts__card">
        <q-skeleton v-if="loading" animation="blink" height="100%" />
        <template v-else>
          <div class="insights-counts__title">
            Invoices (documents)
            <q-icon name="help_outline" size="12px" class="insights-counts__help">
              <q-tooltip max-width="260px">
                Whole invoice documents received in the period. Each invoice may contain
                several lines.
              </q-tooltip>
            </q-icon>
          </div>
          <div class="insights-counts__value">{{ formatNumber(data?.totalInvoices) }}</div>
          <div class="insights-counts__footer">
            Last invoice: {{ formatRelative(data?.lastInvoiceReceivedTime) }}
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { InsightsCountsResult } from '../types';

defineProps<{
  data: InsightsCountsResult | null;
  loading: boolean;
}>();

const formatNumber = (n: number | undefined | null): string => {
  if (n === undefined || n === null) return '--';
  return new Intl.NumberFormat().format(n);
};

const formatRelative = (iso: string | undefined | null): string => {
  if (!iso) return '--';
  const date = new Date(iso);
  const diffMs = Date.now() - date.getTime();
  if (diffMs < 0) return date.toLocaleString();

  const sec = Math.floor(diffMs / 1000);
  const min = Math.floor(sec / 60);
  const hr = Math.floor(min / 60);
  const day = Math.floor(hr / 24);

  if (day > 0) return `${day} day${day > 1 ? 's' : ''} ago`;
  if (hr > 0) return `${hr} hour${hr > 1 ? 's' : ''} ago`;
  if (min > 0) return `${min} minute${min > 1 ? 's' : ''} ago`;
  return 'just now';
};
</script>

<style scoped lang="scss">
.insights-counts {
  &__intro {
    margin: 0 0 12px;
    font-size: 13px;
    color: #4c4c4c;

    &-hint {
      display: block;
      margin-top: 2px;
      font-size: 12px;
      color: #8b8b8b;
    }
  }

  &__help {
    color: #8b8b8b;
    cursor: help;
    margin-left: 4px;
    vertical-align: middle;
  }

  &__grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
    gap: 12px;
  }

  &__card {
    display: flex;
    flex-direction: column;
    min-height: 100px;
    border: 1px solid #e0e0e0;
    border-radius: 10px;
    overflow: hidden;
  }

  &__title {
    padding: 10px;
    font-size: 12px;
    line-height: 1.2;
    font-weight: 500;
    color: #707070;
  }

  &__value {
    padding: 5px 10px;
    font-size: 22px;
    font-weight: 500;
    line-height: 1.2;
    color: #4c4c4c;
  }

  &__footer {
    margin-top: auto;
    padding: 5px 10px;
    font-size: 12px;
    line-height: 1.2;
    font-weight: 500;
    background: #d9d9d9;
    color: #707070;
  }
}
</style>
